"use client";

import React, { useState } from "react";
import styles from "./VideoGrid.module.scss";
import VideoCard from "@components/molecules/VideoCard";
import VideoModal from "@components/molecules/VideoModal/VideoModal";
import Text from "@components/atoms/Text";
import {
  VideoGridProps,
  VideoSource,
  VideoMetadata,
  DeviceType,
} from "@interfaces/video";
import { createVideoSource } from "@utils/videoUtils";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { updateAppBar } from "@hooks/useWebviewCallback";
import { appbarConfig, CITY_SELECTION_MODAL_APPBAR_CONFIG } from "@utils/appbarConfig";
import { getAppSelectCityRedirectUrl } from "@helpers/client/getAppSelectCityRedirectUrl";
import assetLoader from "@utils/internalImageLoder";
import { usePathname } from "next/navigation";

const VideoGrid: React.FC<VideoGridProps> = ({
  title,
  videos,
  className,
  isLoggedIn,
  brandName,
  modelName,
  cityName,
  carAvailability,
  pageType,
}) => {
  const pathname = usePathname();
  const [selectedVideo, setSelectedVideo] = useState<VideoSource | null>(null);
  const [selectedMetadata, setSelectedMetadata] =
    useState<VideoMetadata | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>(DeviceType.DESKTOP);
  const [currentVideoTime, setCurrentVideoTime] = useState<number>(0);

  const mmAppbarConfig = appbarConfig({
    backgroundColor: '#121212',
    backButtonColor: '#ffffff',
    isAppbarTransparent: false,
    statusIconBrightness: 'light',
    menuItems: [
      {
        iconUrl: `${assetLoader()}/icons/location-pin-light.webp`,
        actionName: '',
        redirectUrl: getAppSelectCityRedirectUrl(pathname),
        ctaText: cityName,
      },
    ],
  });

  // Handle video card click
  const handleVideoClick = async (
    url: string,
    metadata: VideoMetadata,
    index?: number
  ) => {
    setSelectedVideo(createVideoSource(url));
    updateAppBar(CITY_SELECTION_MODAL_APPBAR_CONFIG);
    setSelectedMetadata(metadata);
    setIsModalOpen(true);
  };

  // Handle modal close
  const handleModalClose = async () => {
    if (selectedMetadata) {

      trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
        pageType: pageType,
        additionalProps: {
          action_type: "tap_btn",
          action_name: "tap_btn_close_expert_video",
          video_tag: selectedMetadata?.title || "",
          closing_time: Math.floor(currentVideoTime), // Video position in seconds (e.g., 90)
          total_time: selectedMetadata?.duration,
          car_availability: carAvailability,
          city_name: cityName,
          vehicle_make: brandName,
          vehicle_model: modelName,
        },
      });
    }

    updateAppBar(mmAppbarConfig);

    setIsModalOpen(false);
    setSelectedVideo(null);
    setSelectedMetadata(null);
    setCurrentVideoTime(0); // Reset video time
  };

  return (
    <>
      <div className={`${styles.container} ${className || ""}`}>
        <div className={styles.headingWrapper}>
          <div className={styles.leftHorizontalLine}></div>
          <Text level="p" className={styles.sectionTitle}>{title}</Text>
          <div className={styles.rightHorizontalLine}></div>
        </div>

        <div className={styles.mobileLayout}>
          <div className={styles.videoGrid}>
            {videos.map((video: any, index: any) => (
              <VideoCard
                key={video.id}
                video={video}
                metadata={video?.metadata}
                onClick={() => handleVideoClick(video?.url, video?.metadata)}
                showBadges={true}
                isLoggedIn={isLoggedIn}
                brandName={brandName}
                modelName={modelName}
                cityName={cityName}
                carAvailability={carAvailability}
                pageType={pageType}
              />
            ))}
          </div>
        </div>
        <div className={styles.desktopLayout}>
          <div className={styles.desktopVideoGrid}>
            {videos.map((video: any, index: any) => (
              <VideoCard
                key={video.id}
                video={video}
                metadata={video?.metadata}
                onClick={() => handleVideoClick(video?.url, video?.metadata)}
                showBadges={true}
                isLoggedIn={isLoggedIn}
                brandName={brandName}
                modelName={modelName}
                cityName={cityName}
                carAvailability={carAvailability}
                pageType={pageType}
              />
            ))}
          </div>
        </div>
      </div>
      <VideoModal
        video={selectedVideo}
        metadata={selectedMetadata}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        deviceType={deviceType}
        config={{
          autoplay: true,
          muted: false,
          showTitle: true,
          showProgress: true,
          showVolume: true,
          showClose: true,
        }}
        events={{
          onPlay: () => console.log("Video started playing"),
          onPause: () => console.log("Video paused"),
          onEnded: () => console.log("Video ended"),
          onError: (error) => console.error("Video error:", error),
          onTimeUpdate: (currentTime: number) =>
            setCurrentVideoTime(currentTime),
        }}
      />
    </>
  );
};

export default VideoGrid;
